import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';

import { LaunchpoolStatus } from '@flipster/apis/v1/launchpool/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useV1Currencies } from '@flipster/features/src/shared/currency/use-v1-currencies';
import { launchpoolItemBunja, useLaunchpool } from '@flipster/features/src/shared/launchpool/state';
import { showErrorDialog } from '@flipster/features/src/shared/show-error-dialog';
import { FormattedMessage } from '@flipster/i18n';
import { useRouter } from '@flipster/i18n/routing';
import { Dialog, dialog } from '@flipster/react/design-system/dialog';
import { snackbar } from '@flipster/react/design-system/snackbar';
import { TextButton } from '@flipster/react/design-system/text-button';
import Formatted from '@flipster/react/design-utils/formatted';
import DocumentHistoryIcon from '@flipster/react/icon/generated/DocumentHistoryIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css, cx } from '@flipster/styled-system/css';
import { center, flex, hstack } from '@flipster/styled-system/patterns';
import { token } from '@flipster/styled-system/tokens';
import segment from '@flipster/utils/segment';
import { StakeEvent } from '@flipster/utils/segment/events/stake';

import { closedColorCssRaw, dividerCssRaw } from '@web/app/[locale]/(web)/launchpool/_lists/_Item/shared-css';

const TotalReward = () => {
  const t = useTranslations();
  const router = useRouter();

  const { isLoggedIn } = useAuth();
  const { currenciesKeyByCurrency } = useV1Currencies();
  const { launchpoolItemAtom } = useBunja(launchpoolItemBunja);
  const launchpool = useAtomValue(launchpoolItemAtom);
  const { postLaunchPoolUnstakeApiMutation } = useLaunchpool();

  const { id, stakedAmount, stakingCurrency, rewardCurrency, estimatedReward, status, isUnstakable } = launchpool;
  const rewardCurrencyPrecision = currenciesKeyByCurrency?.[rewardCurrency]?.blockchainInfo[0]?.withdrawalPrecision;

  const isUpcoming = status === LaunchpoolStatus.Upcoming;
  const isClosed = status === LaunchpoolStatus.Closed;
  const isShowLaunchpoolHistory = status !== LaunchpoolStatus.Upcoming && Number(stakedAmount) !== 0;

  const postUnstake = async (currency: string) => {
    segment.track(StakeEvent.UnstakeConfirmClick, { symbol: currency });
    try {
      await postLaunchPoolUnstakeApiMutation.mutateAsync(id);
      snackbar.success({ message: `Unstaked in ${currency}` });
    } catch (error) {
      showErrorDialog(error);
    }
  };

  const handleClickUnstake = async () => {
    segment.track(StakeEvent.UnstakeTextLinkClick, { symbol: stakingCurrency });
    if (isUpcoming) {
      await postUnstake(stakingCurrency);
      return;
    }

    dialog.open({
      title: {
        label: 'Unstake',
      },
      content: (
        <p className={css({ color: 'content.state.warning' })}>
          <FormattedMessage id='Autogen_common.r251126.s_If_you_unstake_now_your_rewards_will_be_reset_to_0_Would_you_like_to_proceed' />
        </p>
      ),
      buttons: {
        primary: (
          <Dialog.Button
            hierarchy='primary'
            label={<FormattedMessage id='Autogen_Common.r251126.s_Unstake' />}
            onClick={() => postUnstake(stakingCurrency)}
          />
        ),
        secondary: (
          <Dialog.Button
            hierarchy='secondary'
            color='neutral'
            label={<FormattedMessage id='Common.r250401.Cancel' />}
          />
        ),
      },
    });
  };

  const onClickLaunchpoolHistory = () => {
    router.push(`/history/transaction/launchpool-reward?launchpoolId=${id}`);
  };

  const onClickRewardInfoButton = () => {
    dialog.open({
      title: {
        label: <FormattedMessage id='Reward_hub.r251015.Your_total_reward' />,
      },
      content: (
        <div className={css({ textStyle: 'Text/L/Regular', color: 'content.default.level-3' })}>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_Rewards_are_calculated_proportional_to_your_share_of_the_total_staked_amount_and_the_trading_multiplier_daily_trading_volume_or_VIP_status' />
          </p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5', mt: 16, mb: 4 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Period' />
          </p>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_Refreshed_daily_at_00_00_UTC' />
          </p>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_There_may_be_a_delay_of_a_few_minutes' />
          </p>
          <p className={css({ textStyle: 'Text/L/Strong', color: 'content.default.level-5', mt: 16, mb: 4 })}>
            <FormattedMessage id='Autogen_common.r251126.s_Duration' />
          </p>
          <p>
            <FormattedMessage id='Autogen_common.r251126.s_If_staking_is_maintained_until_the_end_rewards_will_be_paid_out_If_you_unstake_early_no_rewards_will_be_earned' />
          </p>
        </div>
      ),
      buttons: {
        primary: <Dialog.Button hierarchy='primary' label='OK' />,
      },
    });
  };

  return (
    <>
      <div className={css(dividerCssRaw, { mt: 16, mb: 12 })} />
      <div>
        <div className={css({ pos: 'relative' })}>
          <button
            type='button'
            onClick={onClickLaunchpoolHistory}
            disabled={!isShowLaunchpoolHistory}
            className={center({
              pos: 'absolute',
              top: 0,
              right: 0,
              color: 'content.default.level-3',
              _disabled: { color: 'content.interaction.disabled' },
            })}
          >
            <DocumentHistoryIcon size={24} />
          </button>

          <div
            className={cx(
              css({
                display: 'flex',
                flexDirection: { base: 'column', tablet: 'row' },
                justifyContent: 'center',
                alignItems: { tablet: 'center' },
                gap: { base: 8, tablet: 12 },
                color: 'content.default.level-5',
              }),
              isClosed && css(closedColorCssRaw),
            )}
          >
            <div className={hstack({ textStyle: 'Text/M/Strong', h: 24 })}>
              <FormattedMessage id='Reward_hub.r251015.Your_total_reward' />
              <button
                type='button'
                onClick={onClickRewardInfoButton}
                className={css({ buttonShape: 'inline-icon', ml: 4 })}
              >
                <InformationCircleIcon size={16} color={token('colors.content.default.level-1')} />
              </button>
            </div>
            <div>
              <Formatted.Number
                value={estimatedReward}
                suffix={<span className={css({ textStyle: 'Text/M/Regular' })}>{rewardCurrency}</span>}
                precision={rewardCurrencyPrecision}
                zeroString='-'
                css={hstack.raw({
                  textStyle: 'Title/S',
                  justify: { tabletDown: 'flex-end' },
                })}
              />
            </div>
          </div>
          {!isClosed && (
            <div className={flex({ justify: 'end', py: 8 })}>
              <TextButton
                size='medium'
                color='passive'
                onClick={handleClickUnstake}
                disabled={postLaunchPoolUnstakeApiMutation.isPending || !isLoggedIn || !isUnstakable}
                label={t('Autogen_common.r251126.s_Unstake')}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default TotalReward;
